import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type Step = {
  id: number;
  title: string;
  description: string;
};

const steps: Step[] = [
  {
    id: 1,
    title: "Submit Request",
    description:
      "Fill out our simple online form with your basic details.",
  },
  {
    id: 2,
    title: "Document Collection",
    description:
      "Upload required docs. Our experts will verify everything.",
  },
  {
    id: 3,
    title: "Application Processing",
    description:
      "We submit to the embassy and book your appointment if needed.",
  },
  {
    id: 4,
    title: "Visa Approval",
    description:
      "Receive your passport with the approved visa.",
  },
];

const ApplicationSteps: React.FC = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveStep((currentStep) =>
        currentStep === steps.length ? 1 : currentStep + 1,
      );
    }, 2200);

    return () => window.clearInterval(timer);
  }, []);

  const progressPercent =
    steps.length > 1 ? ((activeStep - 1) / (steps.length - 1)) * 100 : 0;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-dark mb-4">
            {t("How It Works")}
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto">
            {t("Four simple steps to get your visa approved.")}
          </p>
        </div>

        <div className="relative">
          
          {/* Connecting Line */}
          <div className="hidden md:block absolute left-[12.5%] right-[12.5%] top-8 z-0">
            <div className="relative h-0.5 bg-gray-100">
              <div
                className="absolute left-0 top-0 h-full bg-brand-pink/40 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
              <div
                className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white bg-brand-pink shadow-lg shadow-brand-pink/30 transition-all duration-500"
                style={{ left: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative z-10">

            {steps.map((step) => (
              <button
                key={step.id}
                type="button"
                onClick={() => setActiveStep(step.id)}
                onMouseEnter={() => setActiveStep(step.id)}
                onFocus={() => setActiveStep(step.id)}
                className="w-full text-center"
              >
                 
                 {/* Step Circle */}
                 <div
                  className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-xl font-bold mb-6 transition-all duration-300 ${
                    step.id === activeStep
                      ? "border-4 border-brand-pink bg-brand-pink text-white shadow-lg shadow-brand-pink/20 scale-105"
                      : "border-4 border-gray-200 bg-white text-gray-400"
                   }`}
                 >
                   {step.id}
                 </div>

                 {/* Title */}
                 <h4
                   className={`font-bold text-lg mb-2 transition-colors ${
                     step.id === activeStep ? "text-brand-pink" : "text-brand-dark"
                   }`}
                 >
                   {t(step.title)}
                 </h4>

                 {/* Description */}
                 <p
                   className={`text-sm transition-colors ${
                     step.id === activeStep ? "text-gray-700" : "text-gray-500"
                   }`}
                 >
                   {t(step.description)}
                 </p>
              </button>
            ))}

          </div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationSteps;
